import { inject, Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { PhitoSessionService } from './phito-session.service';
import { AppService } from '../app/app.service';
import { LoginService } from '../modules/login/login.service';
import { AppConfigService } from './app-config.service';
import { NotificationService } from './notification.service';

@Injectable({ providedIn: 'root' })
export class PhitoSecurityInterceptor implements HttpInterceptor {
    private config = inject(AppConfigService);
    private rtr = inject(Router);
    private phitoSessionService = inject(PhitoSessionService);
    private appService = inject(AppService);
    private loginService = inject(LoginService);
    private notificationService = inject(NotificationService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.appService.incrementLoadingCount();
        let headers = this.getSecurityHeaders(request.headers);
        let authRequest = request.clone({ setHeaders: headers });

        return next.handle(authRequest).pipe(
            finalize(() => {
                this.appService.decrementLoadingCount();
            }),
            tap(
                (event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        const backendVersion = event.headers?.get('Backend-Version');
                        if (
                            backendVersion &&
                            backendVersion != '§§version§§' &&
                            this.config.getConfig().VERSION !== 'dev' &&
                            this.config.getConfig().VERSION !== backendVersion
                        ) {
                            this.notificationService.updateMessage(backendVersion).subscribe(() => {
                                this.phitoSessionService.sessionAktualisieren(true);
                            });
                        }

                        const sessionOutdated = event.headers?.get('session-outdated');
                        if (sessionOutdated) {
                            this.phitoSessionService.sessionAktualisieren(false).subscribe({
                                next: (aktualisiert: boolean) => {
                                    if (aktualisiert) {
                                        this.messageSessionOutdated();
                                    }
                                },
                            });
                        }
                    }
                },
                (err: any) => {
                    if (err instanceof HttpErrorResponse) {
                        if (err.status === 0 || err.status === 503) {
                            console.log('Keine Verbindung zum Server oder Server startet');
                        } else if (request.headers.has('CHECK-SESSION-VALID')) {
                            this.phitoSessionService.removePhitoSession();
                        } else if (err.status === 401) {
                            let params = { targetUrl: this.rtr.url };
                            this.loginService.logout(true, params);
                        }
                    } else if (err.name === 'TimeoutError') {
                        alert('Timeout occured! \nPlease check your network/internet connection.');
                    }
                },
            ),
        );
    }

    private messageSessionOutdated() {
        this.notificationService.infoMessage('Benutzereinstellungen angepasst.', {
            buttonText: 'Bitte Aktualisieren',
            duration: 0,
        }).subscribe(
            () => {
                location.reload();
            },
        );
    }

    private getSecurityHeaders(headers: HttpHeaders): any {
        let headersString = {};

        if (headers.get('TIMEZONE') == null) {
            headersString['TIMEZONE'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }

        // headersString['ALIENATE_NAMES'] = true;
        const sessionToken = this.phitoSessionService.xsrfToken();
        if (sessionToken) {
            if (!headers.has('NO-XSRF-TOKEN')) {
                if (headers.get('X-XSRF-TOKEN') == null) {
                    headersString['X-XSRF-TOKEN'] = sessionToken;
                }
            }

            const userDto = this.phitoSessionService.getRepresentative();
            if (userDto?.userHexId) {
                if (!headers.has('REPRESENTATIVE-HEX-ID')) {
                    headersString['REPRESENTATIVE-HEX-ID'] = userDto.userHexId;
                }
            }
        } else if (this.phitoSessionService.apiKey()) {
            headersString['API-KEY'] = this.phitoSessionService.apiKey();
        }
        if (headers.has('Content-Type')) {
            headersString['Content-Type'] = headers.get('Content-Type');
        } else {
            let phitoType = headers.get('Phito-Type');
            if (phitoType == 'fileupload') {
                // kein Content Type setzen
                headersString['Phito-Type'] = phitoType;
            } else if (phitoType === 'NO-XSRF-TOKEN') {
                delete headersString['X-XSRF-TOKEN'];
                delete headers['X-XSRF-TOKEN'];
            } else {
                headersString['Content-Type'] = 'application/json';
            }
        }
        headersString['PRODUCTION-SYSTEM'] = this.config.getConfig().PROD === true ? 'true' : 'false';

        return headersString;
    }
}
